// Typed errors for dashboard API responses (SEC-005).
//
// Gated intelligence routes deny with HTTP 403 and one of two error kinds:
//   - 'tier_locked'       { error, error_kind, tool, tier_required_to_unlock }
//                         the plan is too low → render an upgrade prompt;
//   - 'tool_not_entitled' { error, error_kind, tool }
//                         the plan covers the view but the account's explicit
//                         entitlements do not → no upgrade prompt (it would not help).
// Pages convert these into typed errors so they can render the right notice, and so
// SWR does not keep retrying a request that will be denied until the account changes.

// Mirrors SubscriptionTier in packages/shared; the SPA does not import the shared
// runtime package, so the list is duplicated here and pinned by
// tests/dashboard/api-errors.test.ts against ENTITLEMENT_MAP.
export const PLAN_TIERS = ['explorer', 'professional', 'team', 'enterprise'] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

/** The plan does not include this view; upgrading to `requiredTier` unlocks it. */
export class TierLockedError extends Error {
  readonly requiredTier: PlanTier | null;

  constructor(message: string, requiredTier: PlanTier | null) {
    super(message);
    this.name = 'TierLockedError';
    this.requiredTier = requiredTier;
  }
}

/** The plan covers this view but the account's entitlements exclude it. */
export class NotEntitledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotEntitledError';
  }
}

function isPlanTier(value: unknown): value is PlanTier {
  return typeof value === 'string' && (PLAN_TIERS as readonly string[]).includes(value);
}

/**
 * Build the Error a page should throw for a non-OK response.
 * `body` is the parsed JSON (or null when the body was not JSON).
 */
export function apiErrorFromResponse(status: number, body: unknown, fallbackMessage: string): Error {
  const record = body !== null && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  const message = typeof record?.error === 'string' && record.error.length > 0 ? record.error : fallbackMessage;
  if (status === 403 && record?.error_kind === 'tier_locked') {
    return new TierLockedError(message, isPlanTier(record.tier_required_to_unlock) ? record.tier_required_to_unlock : null);
  }
  if (status === 403 && record?.error_kind === 'tool_not_entitled') {
    return new NotEntitledError(message);
  }
  return new Error(message);
}

/** SWR `shouldRetryOnError`: never retry an access denial. */
export function shouldRetryApiError(error: Error): boolean {
  return !(error instanceof TierLockedError || error instanceof NotEntitledError);
}
